import type { AxiosInstance } from "axios";
import { Building2, Eye, EyeOff, Loader2, MailCheck, Store } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "../components/ui/button.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { PasswordStrengthMeter } from "../components/PasswordStrengthMeter.js";
import { cn } from "../lib/cn.js";
import { apiErrorMessage } from "../lib/api.js";
import { evaluatePassword, MIN_PASSWORD_LENGTH } from "../lib/password.js";
import { signup } from "../lib/signup.js";
import { Logo } from "../components/Logo.js";

/**
 * Create an account — the path a merchant takes when they came to the dashboard directly
 * rather than installing from Shopify.
 *
 * IT ENDS ON "CHECK YOUR EMAIL", NOT SIGNED IN. Signup creates the account and mails a
 * verification link; the server refuses login until the address is verified. So the honest
 * end of this screen is "we sent you a link", the same message whether or not the email was
 * already registered — the API answers 200 either way so an address cannot be probed.
 */
export function SignupScreen({ client }: { readonly client: AxiosInstance }): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [orgKind, setOrgKind] = useState<"brand" | "agency">("brand");
  const [password, setPassword] = useState("");
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  // Signup has no confirm field — a typo is recoverable with a reset, unlike a single-use
  // invite link — so validity here is just length. The server remains the authority.
  const check = useMemo(() => evaluatePassword(password, password), [password]);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!check.longEnough) return;
    setBusy(true);
    setError(null);
    try {
      await signup(client, {
        email,
        name,
        password,
        orgKind,
        ...(orgName.trim() === "" ? {} : { orgName: orgName.trim() }),
      });
      setSent(true);
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not create your account."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      {sent ? (
        <div className="mx-auto flex w-full max-w-sm flex-col items-center gap-6 text-center">
          <span className="flex size-14 items-center justify-center rounded-full bg-primary/10 text-primary">
            <MailCheck className="size-8" />
          </span>
          <div>
            <h2 className="text-2xl font-bold">Check your email</h2>
            <p className="text-muted-foreground mt-2 text-sm">
              We sent a verification link to <span className="text-foreground font-medium">{email}</span>. Click it to
              finish setting up your account, then sign in.
            </p>
          </div>
          <Button asChild variant="outline" className="h-11 w-full">
            <a href="/">Back to sign in</a>
          </Button>
        </div>
      ) : (
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-8 text-center">
            <h2 className="text-3xl font-bold">Create your account</h2>
            <p className="text-muted-foreground mt-2 text-sm">Attribution you can check. Free to start.</p>
          </div>

          <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)}>
            <div className="grid gap-1.5">
              <Label htmlFor="name">Your name</Label>
              <Input id="name" required className="h-11" value={name} onChange={(e) => setName(e.target.value)} />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="email">Work email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                placeholder="you@company.com"
                required
                className="h-11"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="org">Company name</Label>
              <Input
                id="org"
                placeholder="Defaults to your name"
                className="h-11"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
              />
            </div>

            {/*
              brand vs agency is the same machinery with different limits — it only changes
              which plans are offered. Asked here so the plan picker after verification is
              already scoped correctly.
            */}
            <div className="grid gap-1.5">
              <Label>Account type</Label>
              <div className="grid grid-cols-2 gap-2">
                <KindOption
                  kind="brand"
                  selected={orgKind === "brand"}
                  onSelect={() => setOrgKind("brand")}
                  icon={<Store className="size-4" />}
                  caption="One store"
                />
                <KindOption
                  kind="agency"
                  selected={orgKind === "agency"}
                  onSelect={() => setOrgKind("agency")}
                  icon={<Building2 className="size-4" />}
                  caption="Many client stores"
                />
              </div>
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={reveal ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="••••••••••••"
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  className="h-11 pr-10"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                {/* type="button" is load-bearing: a bare button inside a form submits. */}
                <button
                  type="button"
                  onClick={() => setReveal((current) => !current)}
                  aria-label={reveal ? "Hide password" : "Show password"}
                  aria-pressed={reveal}
                  className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2 rounded-md p-1 transition-colors"
                >
                  {reveal ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                </button>
              </div>
              <PasswordStrengthMeter check={check} shown={password.length > 0} />
              <p className="text-muted-foreground text-xs">At least {MIN_PASSWORD_LENGTH} characters.</p>
            </div>

            {error !== null && (
              <p className="text-destructive text-sm" role="alert">
                {error}
              </p>
            )}

            <Button type="submit" disabled={busy || !check.longEnough} className="h-11 w-full">
              {busy && <Loader2 className="animate-spin" />}
              {busy ? "Creating…" : "Create account"}
            </Button>

            <p className="text-muted-foreground text-center text-sm">
              Already have an account?{" "}
              <a href="/" className="text-primary font-medium underline underline-offset-4">
                Sign in
              </a>
            </p>
          </form>
        </div>
      )}
    </Shell>
  );
}

function KindOption({
  kind,
  selected,
  onSelect,
  icon,
  caption,
}: {
  readonly kind: "brand" | "agency";
  readonly selected: boolean;
  readonly onSelect: () => void;
  readonly icon: React.ReactNode;
  readonly caption: string;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        "flex flex-col gap-1 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors",
        selected ? "border-primary bg-primary/5 text-primary font-medium" : "hover:bg-accent",
      )}
    >
      <span className="flex items-center gap-1.5 capitalize">
        {icon}
        {kind}
      </span>
      <span className="text-muted-foreground text-xs font-normal">{caption}</span>
    </button>
  );
}

/** The shared branded frame — the split card, brand blobs and wordmark from the login screen. */
function Shell({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  return (
    <main className="relative flex min-h-svh items-center justify-center overflow-hidden bg-(--page) px-4 py-10">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -right-24 size-96 rounded-full bg-amber-400 opacity-20 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 size-96 rounded-full bg-blue-500 opacity-20 blur-3xl" />
      </div>

      <div className="absolute top-8 left-8 z-10">
        <Logo className="h-7 w-auto" />
      </div>

      <div className="bg-card relative flex w-full max-w-4xl flex-col overflow-hidden rounded-2xl border shadow-xl md:flex-row">
        {/* The pitch. Hidden below md so the form is never pushed under the fold on a phone. */}
        <div className="hidden flex-col justify-center gap-6 bg-gradient-to-b from-blue-700 to-indigo-800 p-10 text-center text-white md:flex md:w-1/2">
          <h1 className="text-4xl leading-tight font-extrabold">Start measuring what works</h1>
          <p className="text-lg text-blue-100">
            See spend, revenue and ROAS by source — first-click, last-click and data-driven, side by side.
          </p>
          <div className="flex justify-center">
            <span className="rounded-full bg-white/20 px-6 py-2 text-sm font-medium backdrop-blur-sm">
              No card to get started
            </span>
          </div>
        </div>

        <div className="w-full p-8 md:w-1/2 md:p-10">{children}</div>
      </div>
    </main>
  );
}
